import { Core } from "@faire/design-tokens";
import { trackCategoriesBreadcrumbNavClick } from "@faire/web-api/events/categories/click/breadcrumbNav";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { IBreadcrumbOption } from "@faire/web/ui/Breadcrumbs";
import React, { useMemo } from "react";
import styled from "styled-components";

import { Breadcrumb } from "@faire/retailer/components/Breadcrumb";
import { FaireHelmet } from "@faire/retailer/components/Helmet";
import {
  getCategoryBreadcrumbOptions,
  getMetaDescription,
} from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/__internal__/utils";
import { useLoggedOutCategoryPageCurrentCategory } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentCategory";
import { useLoggedOutCategoryPageBrandTileReviewsResponse } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/DataFetching/useLoggedOutCategoryPageBrandTileReviewsResponse";
import { useLoggedOutCategoryPageMetaTags } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/DataFetching/useLoggedOutCategoryPageMetaTags";
import { useLoggedOutCategoryPagePrevAndNextPageNumber } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/hooks/usePrevAndNextPageNumber";

export const LoggedOutCategoryPageHelmetAndBreadcrumb: React.FC = () => {
  const categoryModel = useLoggedOutCategoryPageCurrentCategory();
  const { seoCategoryText } = useLoggedOutCategoryPageMetaTags();
  const { title, meta_description, applied_filter_display_name } =
    seoCategoryText ?? {};
  const { strictLocalize } = useStrictLocalization();
  const brandsWithReview = useLoggedOutCategoryPageBrandTileReviewsResponse();

  const prevAndNextPageNumber = useLoggedOutCategoryPagePrevAndNextPageNumber();

  const breadcrumbOptions = useMemo(() => {
    return categoryModel
      ? getCategoryBreadcrumbOptions(
          categoryModel,
          strictLocalize,
          applied_filter_display_name ?? ""
        )
      : [];
  }, [applied_filter_display_name, categoryModel, strictLocalize]);

  const description = useMemo(() => {
    return getMetaDescription(brandsWithReview, meta_description);
  }, [brandsWithReview, meta_description]);

  return (
    <>
      <FaireHelmet
        title={title}
        description={description}
        prev={prevAndNextPageNumber.prev}
        next={prevAndNextPageNumber.next}
        alignOpenGraphTitleDescription
      />
      <StyledBreadcrumb
        options={breadcrumbOptions}
        onClick={(option: IBreadcrumbOption) => {
          trackCategoriesBreadcrumbNavClick(option.path ?? "");
        }}
      />
      <Spacer height={{ mobileAndAbove: "0", tabletAndAbove: "2x" }} />
    </>
  );
};

const StyledBreadcrumb = styled(Breadcrumb)`
  padding: 14px 20px;

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 14px 25px;
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: 14px 15px;
  }
  border-bottom: 1px solid ${Core.border.muted};
`;
